import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Field,
  Input,
  Select,
  Toast,
} from '@/components/ui';

interface Voice {
  id: string;
  voice_id: string;
  nickname: string;
  vendor_name: string | null;
  notes: string | null;
  provider_id: string | null;
  provider_name: string | null;
  provider_driver: string | null;
  is_default: boolean;
  enabled: boolean;
  /** Which vendor the id's shape suggests, or null when unrecognised. */
  looks_like: string | null;
  /** The id belongs to a different vendor than the provider it is filed under. */
  mismatched: boolean;
}

interface ProviderOption {
  id: string;
  name: string;
  kind: string;
  driver: string;
}

const BLANK = {
  voice_id: '',
  nickname: '',
  vendor_name: '',
  notes: '',
  provider_id: '',
  is_default: false,
  enabled: true,
};

/**
 * The voices an account has saved, under names somebody picked.
 *
 * A voice id is twenty characters of base62. Nobody managing more than one
 * account can tell `zGjIP4SZ1MnY9m93k97r` from `21m00Tcm4TlvDq8ikWAM`, and the
 * portal offered a bare text box — so the only way to find out what a voice
 * sounded like was to place a call and listen.
 *
 * Several per account, because one account runs more than one bot and a
 * screener and a consultative agent should not have to sound the same.
 */
export default function VoiceLibrary({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState({ ...BLANK });
  const [editing, setEditing] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Voice | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(
    null,
  );

  const voices = useQuery({
    queryKey: ['client-voices', tenantId],
    queryFn: () => api.get<{ data: Voice[] }>(`/admin/clients/${tenantId}/voices`),
  });

  const providers = useQuery({
    queryKey: ['tts-providers'],
    queryFn: () => api.get<{ data: ProviderOption[] }>('/admin/providers'),
  });

  const ttsProviders = (providers.data?.data ?? []).filter((p) => p.kind === 'TTS');

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['client-voices', tenantId] });
  };

  const save = useMutation({
    mutationFn: (body: typeof BLANK) =>
      editing
        ? api.put(`/admin/clients/${tenantId}/voices/${editing}`, body)
        : api.post(`/admin/clients/${tenantId}/voices`, body),
    onSuccess: () => {
      refresh();
      setDraft({ ...BLANK });
      setEditing(null);
      setToast({ message: 'Voice saved.', tone: 'good' });
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not save the voice'), tone: 'bad' }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/clients/${tenantId}/voices/${id}`),
    onSuccess: () => {
      refresh();
      setRemoving(null);
      setToast({ message: 'Voice removed.', tone: 'good' });
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not remove it'), tone: 'bad' }),
  });

  const rows = voices.data?.data ?? [];

  return (
    <Card
      title="Voice library"
      description="Voices this account can use, under names you choose."
    >
      {rows.length === 0 ? (
        <p className="text-xs text-ink-500">
          No voices saved. Add one below and it can be picked by name on any bot
          for this account, instead of pasting the ID each time.
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((voice) => (
            <li
              key={voice.id}
              className="flex flex-wrap items-center justify-between gap-2 py-2"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-ink-900 dark:text-ink-50">
                    {voice.nickname}
                  </span>
                  {voice.is_default && <Badge tone="blue">Default</Badge>}
                  {!voice.enabled && <Badge tone="gray">Off</Badge>}
                  {/* A voice id means something to exactly one vendor. Filed
                      under the wrong one, the first call that uses it gets a
                      400 and the caller hears the fallback voice — which is
                      the failure that is hardest to trace, because the bot
                      still speaks. */}
                  {voice.mismatched && (
                    <Badge
                      tone="red"
                      title={`This looks like a ${voice.looks_like} ID but is filed under ${voice.provider_driver}. Calls using it will be rejected.`}
                    >
                      Wrong vendor
                    </Badge>
                  )}
                </div>
                <p className="mt-0.5 font-mono text-2xs text-ink-500">
                  {voice.voice_id}
                  {voice.provider_name && ` · ${voice.provider_name}`}
                  {voice.vendor_name && ` · ${voice.vendor_name}`}
                </p>
                {voice.notes && (
                  <p className="mt-0.5 text-2xs text-ink-400">{voice.notes}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setEditing(voice.id);
                    setDraft({
                      voice_id: voice.voice_id,
                      nickname: voice.nickname,
                      vendor_name: voice.vendor_name ?? '',
                      notes: voice.notes ?? '',
                      provider_id: voice.provider_id ?? '',
                      is_default: voice.is_default,
                      enabled: voice.enabled,
                    });
                  }}
                >
                  Edit
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setRemoving(voice)}>
                  Remove
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 border-t border-line pt-4">
        <p className="mb-2 text-xs font-medium text-ink-700 dark:text-ink-300">
          {editing ? 'Edit voice' : 'Add a voice'}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name" hint="What you will see when picking it.">
            <Input
              value={draft.nickname}
              placeholder="Sarah — warm, female"
              onChange={(e) => setDraft({ ...draft, nickname: e.target.value })}
            />
          </Field>
          <Field label="Voice ID" hint="Copied from the provider.">
            <Input
              value={draft.voice_id}
              placeholder="zGjIP4SZ1MnY9m93k97r"
              className="font-mono text-xs"
              onChange={(e) => setDraft({ ...draft, voice_id: e.target.value.trim() })}
            />
          </Field>
          <Field
            label="Provider"
            hint="Which vendor this ID belongs to. Mismatches are flagged rather than refused."
          >
            <Select
              value={draft.provider_id}
              onChange={(e) => setDraft({ ...draft, provider_id: e.target.value })}
            >
              <option value="">Account default</option>
              {ttsProviders.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Notes" hint="Optional.">
            <Input
              value={draft.notes}
              placeholder="Used on the ACA screener"
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
          </Field>
        </div>

        <label className="mt-3 flex items-center gap-2 text-xs text-ink-600 dark:text-ink-400">
          <input
            type="checkbox"
            checked={draft.is_default}
            onChange={(e) => setDraft({ ...draft, is_default: e.target.checked })}
          />
          Use this voice when a bot names none of its own
        </label>

        <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            loading={save.isPending}
            disabled={!draft.nickname.trim() || !draft.voice_id.trim()}
            onClick={() => save.mutate(draft)}
          >
            {editing ? 'Save changes' : 'Add voice'}
          </Button>
          {editing && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setEditing(null);
                setDraft({ ...BLANK });
              }}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!removing}
        title="Remove this voice?"
        confirmLabel="Remove"
        busy={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.id)}
        body={
          <p className="text-sm">
            <strong>{removing?.nickname}</strong> will be removed from this
            account&apos;s library. Any bot pointed at it falls back to its own
            voice rather than going silent, so no campaign breaks — but those
            bots will sound different on the next call.
          </p>
        }
      />

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </Card>
  );
}
